// Shared subdomain detection and configuration types

export interface SubdomainConfig {
  id: string;
  subdomain: string;
  affiliationId: string;
  affiliationName: string;
  /** Which whitelabel template this tenant gets (see shared/config/orgTemplates.ts).
   *  Required at creation; tenants created before this field existed resolve to
   *  "InstitutionAlumni". */
  institutionType?: string;
  /** Optional organization this subdomain serves. Lets several organizations
   *  under the same affiliation run distinct whitelabels. Access is still
   *  gated by affiliationId; org-aware surfaces use organizationId, falling
   *  back to affiliationId (affiliation-as-tenant). */
  organizationId?: string;
  organizationName?: string;
  brandName?: string;
  logoUrl?: string;
  logoFileName?: string;
  iconUrl?: string; // PWA icon URL (separate from logo)
  iconFileName?: string;
  pwaIconsGenerated?: boolean; // Whether all PWA icon sizes have been generated
  pwaIconsCount?: number; // Number of generated PWA icon sizes
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  /** Page canvas / card surface / body text — optional theme tokens applied as
   *  CSS variables. Defaults preserve the current light look. */
  canvasColor?: string;
  surfaceColor?: string;
  textColor?: string;
  tagline?: string;
  fontFamily?: string;
  /** Stylesheet URL for custom fonts (allowlisted CDNs only — Google Fonts /
   *  Bunny Fonts). Loaded by the whitelabel app at boot. */
  fontCssUrl?: string;
  borderRadius?: string;
  customComponents?: Record<string, any>;
  customCss?: string;
  /** Alumni-institution tenants only: show the big/little Lineage section on profiles.
   *  Off by default; clubs never show it regardless. */
  showLineage?: boolean;
  favicon?: string;
  description?: string;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
  createdBy?: string;
  updatedBy?: string;
}

export interface BrandingConfig extends SubdomainConfig {
  // Extended interface for branding context
  isWhitelabel: boolean;
}

/**
 * Detect subdomain from current hostname
 */
export function getSubdomain(): string | null {
  if (typeof window === "undefined") return null;

  const hostname = window.location.hostname;

  const parts = hostname.split(".");

  // Handle localhost patterns: subdomain.localhost or subdomain.localhost:port
  const isLocalhost = hostname.includes("localhost");

  if (isLocalhost && parts.length >= 2 && parts[0] !== "localhost") {
    const subdomain = parts[0];
    return subdomain;
  }

  // Handle production patterns: subdomain.zurilink.com
  if (!isLocalhost && parts.length >= 3) {
    const subdomain = parts[0];
    // Exclude common www prefix
    if (subdomain !== "www") {
      return subdomain;
    }
  }

  return null;
}

/**
 * Check if current page is accessed via subdomain
 */
export function isSubdomainAccess(): boolean {
  return getSubdomain() !== null;
}

/**
 * Get base domain without subdomain
 */
export function getBaseDomain(): string {
  if (typeof window === "undefined") return "";

  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  if (hostname.includes("localhost")) {
    return (
      "localhost" + (window.location.port ? `:${window.location.port}` : "")
    );
  }

  // For production, return the main domain
  if (parts.length >= 2) {
    return parts.slice(-2).join(".");
  }

  return hostname;
}

/**
 * Generate CSS custom properties from subdomain config
 */
export function generateCustomCSSProperties(
  config: SubdomainConfig
): Record<string, string> {
  const properties: Record<string, string> = {};

  if (config.primaryColor) {
    properties["--brand-primary"] = config.primaryColor;
  }

  if (config.secondaryColor) {
    properties["--brand-secondary"] = config.secondaryColor;
  }

  if (config.accentColor) {
    properties["--brand-accent"] = config.accentColor;
  }

  if (config.fontFamily) {
    properties["--brand-font-family"] = config.fontFamily;
  }

  if (config.borderRadius) {
    properties["--brand-border-radius"] = config.borderRadius;
  }

  return properties;
}

/**
 * Apply custom branding to document
 */
export function applyCustomBranding(config: SubdomainConfig): void {
  if (typeof document === "undefined") return;

  // Update document title
  document.title = config.brandName || config.affiliationName || "ZuriLink";

  // Apply CSS custom properties
  const properties = generateCustomCSSProperties(config);
  const root = document.documentElement;

  Object.entries(properties).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });

  // Add branding class to body
  document.body.classList.add("whitelabel-branded");
  document.body.setAttribute("data-subdomain", config.subdomain);
}

/**
 * Remove custom branding from document
 */
export function removeCustomBranding(): void {
  if (typeof document === "undefined") return;

  // Reset document title
  document.title = "ZuriLink";

  // Remove branding class
  document.body.classList.remove("whitelabel-branded");
  document.body.removeAttribute("data-subdomain");

  // Remove CSS custom properties
  const root = document.documentElement;
  const propertiesToRemove = [
    "--brand-primary",
    "--brand-secondary",
    "--brand-accent",
    "--brand-font-family",
    "--brand-border-radius",
  ];

  propertiesToRemove.forEach((property) => {
    root.style.removeProperty(property);
  });
}

/**
 * Validate subdomain config
 */
export function validateConfig(config: any): config is SubdomainConfig {
  return (
    config &&
    typeof config === "object" &&
    typeof config.id === "string" &&
    typeof config.subdomain === "string" &&
    typeof config.affiliationId === "string" &&
    typeof config.affiliationName === "string" &&
    typeof config.isActive === "boolean"
  );
}

/**
 * Gets current subdomain configuration
 * Fetches from database if on a subdomain
 */
export async function getCurrentSubdomainConfig(): Promise<SubdomainConfig | null> {
  const subdomain = getSubdomain();
  if (!subdomain) {
    return null;
  }

  try {
    // Dynamic import to avoid circular dependencies
    const { SubdomainService } = await import("../services/subdomain.service");
    const subdomainService = SubdomainService.getInstance();
    const config = await subdomainService.getSubdomainConfig(subdomain);
    return config;
  } catch (error) {
    console.error("Error getting current subdomain config:", error);
    return null;
  }
}
